package com.advaita.cloud.drive.ui.store

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.shared.FileTreeResponse
import com.advaita.cloud.drive.ui.api.RedirectingException
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

const val SEARCH_DEBOUNCE_MS = 300L
const val SEARCH_MIN_CHARS = 2
const val SEARCH_PAGE_LIMIT = 50

/** Bare text means "contains" (humans typing `fastq` want substring, and a
 *  contains-glob is case-insensitive server-side). Explicit wildcards pass through. */
fun toSearchPattern(input: String): String =
    if (input.any { it == '*' || it == '?' }) input else "*$input*"

/**
 * State + logic for the spotlight search modal (ADV-1215). DOM-free on purpose:
 * jsTest runs under Node, so the fetch is injected and the scope is injectable
 * for virtual-time tests. The modal component is a thin renderer over this.
 */
class SearchStore(
    private val search: suspend (path: String, namePattern: String, limit: Int) -> FileTreeResponse,
    /**
     * ADV-1330 (Tim, 2026-09-28): the search inside a shared folder, by its id. `?path=` is always
     * the caller's own drive, so a shared folder is searched with `GET /api/files?folderId=`
     * (recursive, one `read` check on the folder).
     */
    private val searchFolder: suspend (folderId: String, namePattern: String, limit: Int, pageToken: String?) -> FileTreeResponse =
        { _, _, _, _ -> error("no shared-folder search") },
    private val scope: CoroutineScope = MainScope(),
) {
    var isOpen: Boolean by mutableStateOf(false)
        private set
    var query: String by mutableStateOf("")
        private set
    var results: List<FileDto> by mutableStateOf(emptyList())
        private set
    var isSearching: Boolean by mutableStateOf(false)
        private set
    var hasMore: Boolean by mutableStateOf(false)
        private set
    var error: String? by mutableStateOf(null)
        private set
    var activeIndex: Int by mutableStateOf(0)
        private set
    var scopePath: String by mutableStateOf("/")
        private set

    /** ADV-1330: the shared folder the search runs in, or null for My Drive at [scopePath]. */
    var sharedFolder: FileDto? by mutableStateOf(null)
        private set

    /** ADV-1330: the shared root above [sharedFolder] (`trail[0]`), for the results' location. */
    var sharedRoot: FileDto? by mutableStateOf(null)
        private set

    /** What the modal says it searches: the shared folder's name, or My Drive's path. */
    val scopeName: String get() = sharedFolder?.displayName ?: scopePath

    /**
     * ADV-1330 (review): a shared search whose pages came back empty after the sharee filter,
     * with a next page still there. The modal then says more results may exist, not "No
     * matches".
     */
    var moreMayExist: Boolean by mutableStateOf(false)
        private set

    /**
     * Where a result lives, for its meta line: its parent folder (a folder's own logicalPath
     * is its path, so its parent is shown). In a shared search, the part under the shared
     * root, starting with the root's name, so no folder of the owner's above the root shows.
     * Example: Kai keeps Run 42 under /Projects/2026, and Lena reads "Run 42 / raw".
     */
    fun locationOf(f: FileDto): String {
        val parent = if (f.kind == FileKind.FOLDER) f.logicalPath.substringBeforeLast('/').ifEmpty { "/" } else f.logicalPath
        val root = sharedRoot ?: return parent
        val below = when {
            parent == root.logicalPath -> ""
            parent.startsWith("${root.logicalPath}/") -> parent.removePrefix("${root.logicalPath}/")
            else -> return parent
        }
        return (listOf(root.displayName) + below.split('/').filter { it.isNotBlank() }).joinToString(" / ")
    }

    /** The trimmed query whose successful response is displayed — drives the empty state. */
    var completedQuery: String? by mutableStateOf(null)
        private set

    private var debounceJob: Job? = null
    private var requestSeq = 0

    fun open(path: String) {
        scopePath = path
        sharedFolder = null
        sharedRoot = null
        isOpen = true
    }

    /**
     * ADV-1330: opens the search inside the shared folder [folder] (and its subfolders). [root]
     * is its shared root, whose name starts each result's location.
     */
    fun openShared(folder: FileDto, root: FileDto) {
        sharedFolder = folder
        sharedRoot = root
        isOpen = true
    }

    fun close() {
        isOpen = false
        query = ""
        results = emptyList()
        hasMore = false
        moreMayExist = false
        isSearching = false
        error = null
        activeIndex = 0
        completedQuery = null
        debounceJob?.cancel()
        requestSeq++   // orphan any in-flight response
    }

    fun onQueryChanged(text: String) {
        query = text
        error = null
        debounceJob?.cancel()
        val trimmed = text.trim()
        if (trimmed.length < SEARCH_MIN_CHARS) {
            requestSeq++   // orphan any in-flight response (mirrors close()) — PR #98 review
            results = emptyList(); hasMore = false; moreMayExist = false; isSearching = false; completedQuery = null
            return
        }
        val seq = ++requestSeq
        debounceJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MS)
            isSearching = true
            try {
                val folder = sharedFolder
                val pattern = toSearchPattern(trimmed)
                var resp = if (folder != null) searchFolder(folder.id, pattern, SEARCH_PAGE_LIMIT, null)
                else search(scopePath, pattern, SEARCH_PAGE_LIMIT)
                // ADV-1330 (review): the sharee filter can empty a page that has a next one.
                // Read one more page, not the whole folder.
                val next = resp.nextPageToken
                if (folder != null && resp.items.isEmpty() && next != null) {
                    if (seq != requestSeq) return@launch
                    resp = searchFolder(folder.id, pattern, SEARCH_PAGE_LIMIT, next)
                }
                if (seq != requestSeq) return@launch   // superseded by a newer query
                results = resp.items
                hasMore = resp.nextPageToken != null
                moreMayExist = folder != null && resp.items.isEmpty() && hasMore
                activeIndex = 0
                completedQuery = trimmed
            } catch (e: CancellationException) {
                throw e   // cancelled by a newer keystroke — propagate, never an error state
            } catch (e: RedirectingException) {
                throw e   // navigating to /auth/login; not an error state
            } catch (t: Throwable) {
                if (seq == requestSeq) error = "Search failed — try again."
            } finally {
                if (seq == requestSeq) isSearching = false
            }
        }
    }

    fun moveSelection(delta: Int) {
        if (results.isNotEmpty()) activeIndex = (activeIndex + delta).coerceIn(0, results.lastIndex)
    }

    /**
     * Where a result click lands. logicalPath is the PARENT path for files but
     * the folder's OWN path for folders (FileEntity.toDto maps folders that way
     * so a click navigates into them) — so both kinds land on logicalPath.
     */
    fun targetFolderOf(f: FileDto): String = f.logicalPath
}
